// riepilogo-api.js — amm-v3 (A3, ondata 30): parte PURA della vista
// "Riepilogo annuale" (pagina 'riepilogo' — carico fiscale dell'anno: KPI,
// per gruppo, distribuzione mensile, STAMPA A5-039, export ZIP), testabile
// sotto node (contract test server/src/lib/amm-v3-riepilogo.test.js).
//
// PRINCIPI (rito A1/A2/proc-v3):
//  - TESTI VERBATIM dal v1 (admin-module.js, renderRiepilogo r.2245-2307):
//    pinnati dal contract test, inclusa la mappa GRP LOCALE del riepilogo
//    (r.2249 — DIVERSA da ammGruppiCatalogo: 7 chiavi con 'altro').
//  - FORMULE del v1 replicate come funzioni pure (maxMese col concat([1]),
//    pct pagato, altezze barre, opzioni anno MOBILI [anno+1, anno, anno-1]).
//  - STATO CONDIVISO col v1: l'anno vive su AMM.riepAnno (default anno
//    corrente, r.748) — flip di flag senza perdere l'anno scelto.
//  - STAMPA (R5 della spike): la vista usa le CLASSI del v1 (amm-no-print /
//    amm-print-only) e window.print(); il beforeprint globale del v1 legge
//    AMM.currentPage — che il wrap del motore tiene aggiornato.
//  - DELEGHE: openQuadraturaSpiegata ('Perché non torna?', modale condiviso
//    col Piano imposte), eur, toast. ZERO bypass di render (censiti: solo il
//    select anno interno) ⇒ NIENTE interceptor.
//  - MAI 'nav:badge' o canali nuovi da client/src/amm-v3/**.

import { apiPathFn } from './api.js';

export const AR_DELEGATED_METHODS = [
  'renderPage',            // il seam stesso (wrap) + refresh post-azione
  'renderRiepilogo',       // la vista v1 wrappata (drift guard)
  'eur',                   // formattazione € (unica fonte: v1)
  'openQuadraturaSpiegata', // 'Perché non torna? ›' (modale condiviso) — resta v1
  'toast',                 // esito export ZIP (stesso canale del v1)
];

export const AR_API_PATHS = [
  'riepilogo',          // GET /riepilogo?anno=
  'export.downloadZip', // 'Esporta tutto (ZIP)' (tetto 200MB propagato dal 413)
];

// ---- TESTI VERBATIM dal v1 (pinnati dal contract test) ----
export const AR_TEXTS = {
  title: 'Riepilogo annuale',
  subPre: 'Carico fiscale ',
  subPost: ' · previsto vs pagato',
  stampatoIl: ' · stampato il ',
  perche: 'Perché non torna? ›',
  stampa: 'Stampa',
  exportZip: 'Esporta tutto (ZIP)',
  kpiPrevisto: 'Totale previsto',
  kpiPagato: 'Pagato finora',
  kpiResiduo: 'Residuo',
  perGruppo: 'Per gruppo',
  pagatoPct: '% pagato',
  emptyPre: 'Nessun importo registrato per il ',
  emptyPost: '. Inserisci gli importi sulle scadenze per popolare il riepilogo.',
  distribuzione: 'Distribuzione mensile (previsto vs pagato)',
  exportZipFallito: 'Export ZIP fallito: ',
};

// mappa gruppi LOCALE del riepilogo — VERBATIM r.2249 (il contract test la
// pinna sul sorgente: se il v1 cambia, qui diventa rosso)
export const AR_GRP = { iva: 'IVA', imposte_dirette: 'Imposte dirette', dipendenti: 'Dipendenti', societario: 'Societario / Camerale', immobili: 'Immobili', estero: 'Estero', altro: 'Altro' };

// colori VERBATIM v1 (KPI r.2262-2264, barre r.2285-2286, fondo barra r.2275)
export const AR_COLORS = { previsto: '#0f172a', pagato: '#059669', residuo: '#0e7490', barPrevisto: '#67e8f9', barPagato: '#0e7490', barBg: '#f1f5f9' };

// ---- STATO condiviso col v1 ----

// l'anno del riepilogo vive SU AMM.riepAnno (inizializzato all'anno corrente
// alla creazione dell'oggetto v1, r.748; difesa se assente)
export function riepAnnoStato(A) {
  if (!A.riepAnno) A.riepAnno = new Date().getFullYear();
  return A.riepAnno;
}

// ---- FORMULE v1 replicate (pure) ----

// massimo mensile del previsto col concat([1]) — r.2250: mai divisione per 0
export function maxMese(perMese) {
  return Math.max.apply(null, (perMese || []).map((m) => m.previsto).concat([1]));
}

// percentuale pagato del gruppo — r.2269: previsto 0 ⇒ 0 (mai NaN)
export function pctPagato(g) {
  return g.previsto ? Math.round(g.pagato / g.previsto * 100) : 0;
}

// altezze barre distribuzione mensile — r.2282
export function barHeights(mm, max) {
  return { hP: Math.round(mm.previsto / max * 100), hPaid: Math.round(mm.pagato / max * 100) };
}

// opzioni del select anno — QUIRK v1 replicato (r.2260): [anno+1, anno,
// anno-1] MOBILI attorno all'anno scelto (si può continuare a scalare)
export function annoOptions(anno) {
  return [anno + 1, anno, anno - 1];
}

// etichetta gruppo — r.2271: mappa locale, fallback chiave grezza
export function gruppoLabel(g) {
  return AR_GRP[g.gruppo] || g.gruppo;
}

// sottotitolo e stato vuoto (testi calcolati r.2253/r.2277)
export function subTesto(anno) {
  return AR_TEXTS.subPre + anno + AR_TEXTS.subPost;
}
export function emptyMessage(anno) {
  return AR_TEXTS.emptyPre + anno + AR_TEXTS.emptyPost;
}

// ---- LETTURA dati (default v1 r.2248 sui totali; difese su liste) ----
export async function readRiepilogo(win, anno) {
  const r = await apiPathFn(win, 'riepilogo').call(null, anno);
  return {
    totali: (r && r.totali) || { previsto: 0, pagato: 0, residuo: 0 },
    perGruppo: (r && r.perGruppo) || [],
    perMese: (r && r.perMese) || [],
  };
}

// ---- AZIONI e DELEGHE (stessi punti del v1) ----
export function esportaZip(win, anno) { return apiPathFn(win, 'export.downloadZip').call(null, anno); }
export function apriQuadratura(win, anno) { win.AMM.openQuadraturaSpiegata(anno, 'riepilogo'); }
